/**
 * Phase 6E — Public Selective Entry landing page.
 * Mirrors IELTS/GMAT design system.
 *
 * NO backend changes — pulls live counts from existing /api/learning/* and /api/exam/* endpoints.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Calculator, BookOpenText, Brain, Trophy, FileText, Sparkles, ArrowRight,
  Zap, ShieldCheck, Target, Timer, MessageSquareQuote, Star, GraduationCap,
} from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import FloatingActionButtons from '@/components/shared/FloatingActionButtons';
import Seo from '@/components/shared/Seo';
import DailyChallengeWidget from '@/components/shared/DailyChallengeWidget';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const SECTIONS = [
  { key: 'maths', icon: Calculator, title: 'Mathematics', meta: '44 topics · 1000+ questions',
    desc: 'Number, Algebra, Geometry, Probability, Logic puzzles, Patterns and Spatial reasoning.', link: '/student/learning?program=selective_entry&subject=mathematics' },
  { key: 'english', icon: BookOpenText, title: 'English', meta: 'Reading · Writing · Comprehension',
    desc: 'Inference, vocabulary, persuasive writing, creative writing, grammar and editing.', link: '/student/learning?program=selective_entry&subject=english' },
  { key: 'practice', icon: Brain, title: 'Practice Questions', meta: 'Topic-tagged · Adaptive · Timed',
    desc: 'Type-by-type drills with instant feedback and worked solutions. Save your progress.', link: '/student/learning' },
  { key: 'mocks', icon: Trophy, title: 'Mock Exams', meta: '90+ full-length papers',
    desc: 'Exam-accurate simulations with real-time timer, percentile and review.', link: '/student/exams' },
  { key: 'resources', icon: FileText, title: 'Resources', meta: 'Formulas · Grammar · Vocab',
    desc: 'Downloadable revision packs, formula sheets, vocab decks and challenge questions.', link: '/resources' },
  { key: 'success', icon: Star, title: 'Success Stories', meta: 'From students like you',
    desc: 'Past offers to John Monash, MacRobertson, Mac.Rob, Suzanne Cory, Nossal and Melbourne High.', link: '/about' },
];

const FEATURES = [
  { icon: Brain, title: 'Adaptive Learning', desc: 'Difficulty self-tunes so practice spends time on exactly the topics holding back the score.' },
  { icon: Timer, title: 'Real-test Simulation', desc: 'All sections with exam-accurate timing, instructions and pacing.' },
  { icon: MessageSquareQuote, title: 'AI Study Coach', desc: 'Stuck? Ask the coach for an easier example, a step-by-step explanation, or 5 fresh practice questions.', badge: 'Live' },
];

const TRUST = [
  { icon: ShieldCheck, label: 'Selective Entry · VCE · Scholarship' },
  { icon: Target, label: 'Percentile estimate' },
  { icon: Zap, label: 'Instant explanations' },
];

const StatBubble = ({ value, label, testid }) => (
  <div className="text-center" data-testid={testid}>
    <div className="text-3xl sm:text-4xl font-bold text-[#D3D86F]">{value}</div>
    <div className="text-[10px] uppercase tracking-[0.18em] text-white/70 mt-1">{label}</div>
  </div>
);

const SelectiveEntryLandingPage = () => {
  const [mobile, setMobile] = useState(false);
  const [stats, setStats] = useState({ topics: '44+', mocks: '90+', resources: '20+' });

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [progs, mocksR, resR] = await Promise.all([
          ax.get('/api/learning/programs').catch(() => ({ data: { items: [] } })),
          ax.get('/api/learning/mocks').catch(() => ({ data: { items: [] } })),
          ax.get('/api/learning/resources').catch(() => ({ data: { items: [] } })),
        ]);
        if (!live) return;
        // Count topics across selective_entry tracks
        const selProg = (progs.data?.items || []).find(p => (p.slug || '').includes('selective') || (p.name || '').toLowerCase().includes('selective'));
        let topicsCount = 44;
        if (selProg) {
          try {
            const tracksR = await ax.get(`/api/learning/programs/${selProg.slug}/tracks`);
            const tracks = tracksR.data?.items || [];
            const counts = await Promise.all(tracks.map(t => ax.get(`/api/learning/tracks/${t.slug}/topics`).catch(() => ({ data: { items: [] } }))));
            topicsCount = counts.reduce((acc, r) => acc + (r.data?.items || []).length, 0) || 44;
          } catch (_e) { /* keep default */ }
        }
        const mocksCount = (mocksR.data?.items || []).length || 90;
        const resCount = (resR.data?.items || []).length || 20;
        setStats({
          topics: `${topicsCount}${topicsCount >= 40 ? '' : '+'}`,
          mocks: `${mocksCount}+`,
          resources: `${resCount}+`,
        });
      } catch (_e) { /* keep defaults */ }
    })();
    return () => { live = false; };
  }, []);

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="selective-landing">
      <Seo title="Selective Entry Preparation · Winhouse Tutors" canonical="/selective"
        description="AI-powered Selective Entry, VCE Selective and Scholarship preparation — original questions, full mock tests, adaptive practice and an AI study coach." />
      <Navigation mobileMenuOpen={mobile} setMobileMenuOpen={setMobile} />
      <FloatingActionButtons />

      <main className="flex-1">
        {/* HERO */}
        <section className="relative overflow-hidden bg-black text-white">
          <div className="absolute inset-0 opacity-90" style={{
            background: 'radial-gradient(60% 50% at 20% 0%, rgba(220,38,38,0.35) 0%, rgba(220,38,38,0) 60%), radial-gradient(50% 40% at 85% 20%, rgba(211,216,111,0.18) 0%, rgba(211,216,111,0) 70%), linear-gradient(180deg, #000 0%, #0a0a0a 100%)',
          }} />
          <div className="relative max-w-6xl mx-auto px-6 pt-28 pb-24 sm:pt-36 sm:pb-32">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 bg-white/10 border border-white/15 backdrop-blur rounded-full px-3 py-1 text-xs uppercase tracking-[0.18em] mb-6">
                <Sparkles className="h-3.5 w-3.5 text-[#D3D86F]" /> Winhouse Selective Entry Learning Centre
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight">
                Selective Entry Prep
                <span className="block text-[#D3D86F]">made calm and structured.</span>
              </h1>
              <p className="text-lg sm:text-xl text-white/75 max-w-2xl mt-6 leading-relaxed">
                Adaptive Mathematics + English practice, full mock papers, downloadable resources and an AI study coach — built for Selective Entry, VCE Selective and Scholarship students in Melbourne.
              </p>

              <div className="mt-6 inline-flex items-center gap-2 bg-[#D3D86F]/10 border border-[#D3D86F]/30 rounded-full pl-3 pr-4 py-2 text-sm sm:text-base">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#D3D86F] text-black flex-shrink-0">
                  <Zap className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <span className="text-white font-semibold">Free Diagnostic</span>
                <span className="text-white/50">+</span>
                <span className="text-white font-semibold">90+ Mock Tests</span>
                <span className="text-[#D3D86F] hidden sm:inline">Available</span>
              </div>

              <div className="flex flex-wrap gap-3 mt-9">
                <Link to="/student/login?next=/student/learning">
                  <Button className="bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-semibold px-7 h-12 text-base rounded-full" data-testid="sel-cta-start">
                    Start Practice <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
                <Link to="/student/login?next=/student/exams">
                  <Button variant="outline" className="bg-white/5 backdrop-blur border-white/25 text-white hover:bg-white/15 hover:text-white px-7 h-12 text-base rounded-full" data-testid="sel-cta-diagnostic">
                    Take Diagnostic Mock
                  </Button>
                </Link>
                <Link to="/get-started">
                  <Button variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10 px-5 h-12 text-base rounded-full" data-testid="sel-cta-book">
                    Book Assessment →
                  </Button>
                </Link>
              </div>

              <div className="flex flex-wrap items-center gap-x-7 gap-y-3 mt-10 text-sm text-white/70">
                {TRUST.map(({ icon: Icon, label }) => (
                  <span key={label} className="inline-flex items-center gap-2">
                    <Icon className="h-4 w-4 text-[#D3D86F]" /> {label}
                  </span>
                ))}
              </div>

              {/* Live stat strip */}
              <div className="grid grid-cols-3 gap-6 mt-12 pt-8 border-t border-white/10 max-w-lg">
                <StatBubble value={stats.topics} label="Topics" testid="sel-stat-topics" />
                <StatBubble value={stats.mocks} label="Mock papers" testid="sel-stat-mocks" />
                <StatBubble value={stats.resources} label="Resources" testid="sel-stat-resources" />
              </div>
            </div>
          </div>
        </section>

        {/* FEATURES */}
        <section className="max-w-6xl mx-auto px-6 py-20">
          {/* Phase 6E.2 — Daily Selective Challenge */}
          <div className="mb-16 -mt-4">
            <DailyChallengeWidget />
          </div>
          <div className="max-w-2xl mb-12">
            <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-3">Why Winhouse Selective</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Built around the way kids actually study.</h2>
            <p className="text-gray-600 mt-3 text-base">Practice → Feedback → Mock → Revise. The cycle repeats, the gaps shrink, the confidence builds.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {FEATURES.map(({ icon: Icon, title, desc, badge }) => (
              <div key={title} className="bg-white border border-gray-200 rounded-2xl p-7 hover:border-black hover:shadow-[0_8px_30px_rgb(0,0,0,0.06)] transition-all flex flex-col" data-testid={`sel-feature-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className="flex items-start justify-between mb-5">
                  <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center">
                    <Icon className="h-5 w-5 text-[#D3D86F]" />
                  </div>
                  {badge && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] font-semibold px-2 py-0.5 rounded-full bg-[#D3D86F]/30 text-black border border-[#D3D86F]">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-700 animate-pulse" /> {badge}
                    </span>
                  )}
                </div>
                <h3 className="font-bold text-lg mb-2">{title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed flex-1">{desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* SECTIONS */}
        <section className="bg-[#FAF9F6] py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-10">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-3">Six sections · One platform</div>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Everything to walk in confident.</h2>
              </div>
              <Link to="/student/login?next=/student/learning">
                <Button variant="outline" className="rounded-full border-black text-black hover:bg-black hover:text-white">
                  Start now <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {SECTIONS.map(({ key, icon: Icon, title, desc, meta, link }) => (
                <Link key={key} to={link} className="block bg-white rounded-2xl border border-gray-200 p-6 hover:border-red-600 hover:-translate-y-0.5 transition-all" data-testid={`sel-section-${key}`}>
                  <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-red-700" />
                  </div>
                  <div className="text-xs uppercase tracking-wider text-gray-400 mb-1.5">{meta}</div>
                  <h3 className="font-bold text-lg mb-2">{title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* PRICING */}
        <section className="max-w-6xl mx-auto px-6 py-20">
          <div className="max-w-2xl mb-10">
            <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-3">Pricing</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Two ways to learn with Winhouse.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <div className="bg-white border border-gray-200 rounded-2xl p-7" data-testid="sel-pricing-1on1">
              <div className="text-xs uppercase tracking-wider text-gray-400 mb-1">Individual sessions</div>
              <div className="flex items-end gap-1">
                <span className="text-4xl font-bold">AUD&nbsp;55</span>
                <span className="text-gray-500 mb-1">/ session</span>
              </div>
              <p className="text-gray-600 mt-3 text-sm">1-on-1 tutoring · subject of choice · weekly or flexible scheduling.</p>
              <Link to="/get-started" className="mt-5 inline-flex items-center text-sm font-semibold text-red-700 hover:text-black">Book a free trial →</Link>
            </div>
            <div className="bg-black text-white rounded-2xl p-7 border border-[#D3D86F]/40" data-testid="sel-pricing-intensive">
              <div className="text-xs uppercase tracking-wider text-[#D3D86F] mb-1">Intensive package</div>
              <div className="flex items-end gap-1">
                <span className="text-4xl font-bold">AUD&nbsp;250</span>
                <span className="text-white/60 mb-1">/ 4 sessions</span>
              </div>
              <p className="text-white/70 mt-3 text-sm">Save vs individual rate · same tutor, structured plan · ideal for exam runup.</p>
              <Link to="/get-started" className="mt-5 inline-flex items-center text-sm font-semibold text-[#D3D86F]">Reserve a package →</Link>
            </div>
          </div>
        </section>

        {/* SUCCESS STORIES STRIP */}
        <section className="bg-[#FAF9F6] py-20">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-xs uppercase tracking-[0.2em] text-red-700 font-semibold mb-3">Past offers</div>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-8">Students placed at top selective schools.</h2>
            <div className="flex flex-wrap gap-2">
              {['John Monash', 'MacRobertson', 'Mac.Rob', 'Suzanne Cory', 'Nossal', 'Melbourne High', 'Glen Waverley SC Accelerated', 'Balwyn High Accelerated'].map(s => (
                <span key={s} className="px-3 py-1.5 bg-white border border-gray-200 rounded-full text-xs font-semibold text-gray-700" data-testid={`sel-school-${s.toLowerCase().replace(/[^a-z]/g,'-')}`}>{s}</span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-black text-white">
          <div className="max-w-4xl mx-auto px-6 py-20 text-center">
            <GraduationCap className="h-10 w-10 text-[#D3D86F] mx-auto mb-4" />
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
              Every question is an <span className="text-[#D3D86F]">original</span>.
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto mb-8 text-base sm:text-lg">
              No recycled material. Crafted by Selective Entry specialists to mirror Year 8/9 entry, VCE Selective Streams and Scholarship exams.
            </p>
            <Link to="/get-started">
              <Button className="bg-[#D3D86F] hover:bg-[#c4ca5e] text-black font-semibold px-7 h-12 rounded-full" data-testid="sel-cta-final">
                Book Assessment <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default SelectiveEntryLandingPage;
